import { useMemo, useState } from 'react';
import { Button, Checkbox, Skeleton, Tree, TreeProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { SearchInput } from 'shared/ui/SearchInput';

import { CustomTreeNode } from '../EntitiesTreeSearch.types';
import { collectKeys } from '../lib/collectKeys';
import { generateList } from '../lib/generateList';
import { getParentTreeKey } from '../lib/getParentTreeKey';
import { RenderTitleNode } from './TreeNodeTitle';
import styles from './EntitiesTreeSearch.module.scss';

interface EntitiesTreeSearchProps {
  treeData: CustomTreeNode[];
  isLoading: boolean;
  handleOk: () => void;
  checkedKeys: string[];
  setCheckedKeys: React.Dispatch<React.SetStateAction<string[]>>;
  removeOldKey: (key: string) => void;
  searchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  showInactive: boolean;
  setShowInactive: React.Dispatch<React.SetStateAction<boolean>>;
  allAvailableKeys: Set<string>;
}

const EntitiesTreeSearch = ({
  treeData,
  isLoading,
  handleOk,
  checkedKeys,
  setCheckedKeys,
  removeOldKey,
  searchValue,
  setSearchValue,
  showInactive,
  setShowInactive,
  allAvailableKeys,
}: EntitiesTreeSearchProps): React.ReactElement => {
  const { t } = useTranslation('ReusableComponents');

  const treeDataKey = useMemo(() => treeData?.[0]?.children?.map((c) => c.key).join(',') || '', [treeData]);

  const [expandedState, setExpandedState] = useState<{ key: string; expanded: React.Key[] }>({
    key: treeDataKey,
    expanded: ['all'],
  });

  const expandedKeys = expandedState.key === treeDataKey ? expandedState.expanded : ['all'];

  const setExpandedKeys = (keys: React.Key[]): void => {
    setExpandedState({ key: treeDataKey, expanded: keys });
  };

  const [autoExpandParent, setAutoExpandParent] = useState(true);

  const visibleKeys = useMemo(() => {
    return new Set(collectKeys(treeData));
  }, [treeData]);

  const allTreeKeys = useMemo(
    () => (allAvailableKeys.size > 0 ? allAvailableKeys : new Set(collectKeys(treeData))),
    [treeData, allAvailableKeys],
  );

  const onCheck: TreeProps['onCheck'] = (checked, info) => {
    const key = info.node.key as string;
    const isAllChecked = checkedKeys.length === 1 && checkedKeys[0] === 'all';
    const visibleKeysList = collectKeys(treeData);

    if (key === 'all' && searchValue !== '') {
      if (info.checked) {
        const newChecked = Array.from(new Set([...checkedKeys, ...visibleKeysList]));
        setCheckedKeys(newChecked);
      } else if (isAllChecked) {
        const invisibleKeys = Array.from(allTreeKeys).filter((k) => k !== 'all' && !visibleKeys.has(k));
        setCheckedKeys(invisibleKeys);
      } else {
        const newChecked = checkedKeys.filter((k) => !visibleKeysList.includes(k));
        setCheckedKeys(newChecked);
      }
      return;
    }

    if (!info.checked) {
      removeOldKey(key);
    }

    const newVisibleChecked = Array.from(checked as string[])
      .map(String)
      .filter((k) => k !== 'all');

    const invisibleCheckedKeys = isAllChecked
      ? Array.from(allTreeKeys).filter((k) => k !== 'all' && !visibleKeys.has(k))
      : checkedKeys.filter((k) => k !== 'all' && !visibleKeys.has(k) && allTreeKeys.has(k));

    const combinedChecked = Array.from(new Set([...invisibleCheckedKeys, ...newVisibleChecked]));
    setCheckedKeys(combinedChecked);
  };

  const visibleCheckedKeys = useMemo(() => {
    const visibleKeysSet = new Set(generateList(treeData).map((item) => String(item.key)));
    return checkedKeys.filter((k) => visibleKeysSet.has(k));
  }, [checkedKeys, treeData]);

  const onExpand = (newExpandedKeys: React.Key[]): void => {
    setExpandedKeys(newExpandedKeys);
    setAutoExpandParent(false);
  };

  const onSearchChange = (value: string): void => {
    setSearchValue(value);
    setAutoExpandParent(true);

    if (value === '') {
      setExpandedKeys(['all']);
      return;
    }

    const localDataList = generateList(treeData);
    const matchingParentKeys = localDataList
      .map((item) => getParentTreeKey(item.key, treeData))
      .filter((key, i, self): key is React.Key => !!key && self.indexOf(key) === i);

    setExpandedKeys(matchingParentKeys);
  };

  return (
    <div className={styles.popupContainer}>
      <div className={styles.searchContainer}>
        <SearchInput
          className={styles.searchInput}
          placeholder={t('entitiesTreeSearch.searchInput.placeholder')}
          onChange={({ target }) => onSearchChange(target.value)}
          value={searchValue}
          allowClear
        />
      </div>
      {isLoading ? (
        <Skeleton loading={isLoading} active paragraph={{ rows: 9, width: '100%' }} className={styles.skeleton} />
      ) : (
        <div className={styles.treeContainer}>
          {treeData?.[0]?.children && treeData?.[0]?.children?.length > 0 ? (
            <Tree
              checkable
              checkedKeys={visibleCheckedKeys}
              onCheck={onCheck}
              treeData={treeData}
              titleRender={RenderTitleNode}
              expandedKeys={expandedKeys}
              selectable={false}
              onExpand={onExpand}
              autoExpandParent={autoExpandParent}
              height={350}
              virtual={false}
              className={styles.tree}
            />
          ) : null}
        </div>
      )}
      <div className={styles.popupFooter}>
        <Checkbox checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}>
          {t('entitiesTreeSearch.showInactive.text')}
        </Checkbox>
        <div className={styles.okBtnContainer}>
          <Button type="primary" size="small" className={styles.okBtn} onClick={handleOk}>
            {t('entitiesTreeSearch.okBtn.text')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EntitiesTreeSearch;
